import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { ApiCredentialDTO, DialerOutboundNumber } from "@tw-pulse/shared";
import { api, ApiClientError } from "@/lib/api";
import { Button } from "@/components/ui/Button";

const keyOf = (n: DialerOutboundNumber) => `${n.connectionId}|${n.phoneNumberSid}`;

type NumbersState =
  | { status: "loading" }
  | { status: "ready"; numbers: DialerOutboundNumber[]; apiBaseUrl: string }
  | { status: "error" };

const API_BASE_PLACEHOLDER = "https://your-tw-pulse-api";
const API_ID_PLACEHOLDER = "$DIALER_API_ID";

/**
 * Developer reference for the place-call API. Renders fully without a connected
 * account (placeholders); once a Twilio account is connected, the curl example
 * is prefilled with a real connection id + voice number.
 */
export function DialerApiDocsPage() {
  const [state, setState] = useState<NumbersState>({ status: "loading" });
  const [fromKey, setFromKey] = useState("");
  const [creds, setCreds] = useState<ApiCredentialDTO[]>([]);

  useEffect(() => {
    let cancelled = false;
    void api.dialer
      .listNumbers()
      .then((res) => {
        if (cancelled) return;
        setState({ status: "ready", numbers: res.numbers, apiBaseUrl: res.apiBaseUrl });
        if (res.numbers[0]) setFromKey(keyOf(res.numbers[0]));
      })
      .catch(() => {
        if (!cancelled) setState({ status: "error" });
      });
    void api.apiCredentials
      .list()
      .then((res) => {
        if (!cancelled) setCreds(res.credentials);
      })
      .catch(() => {
        /* leave empty → curl uses a placeholder api id */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const activeApiId = creds.find((c) => c.status === "active")?.apiId ?? API_ID_PLACEHOLDER;

  const numbers = state.status === "ready" ? state.numbers : [];
  const selected = numbers.find((n) => keyOf(n) === fromKey) ?? numbers[0];
  const live = !!selected;

  const base = state.status === "ready" ? state.apiBaseUrl : API_BASE_PLACEHOLDER;
  const connId = selected?.connectionId ?? "<CONNECTION_ID>";
  const pnSid = selected?.phoneNumberSid ?? "<PHONE_NUMBER_SID>";

  const curl = useMemo(
    () =>
      [
        `curl -X POST "${base}/v1/dialer/connections/${connId}/numbers/${pnSid}/calls" \\`,
        `  -u "${activeApiId}:$DIALER_SECRET" \\`,
        `  -H "Content-Type: application/json" \\`,
        `  -d '{`,
        `    "to": "+15551234567",`,
        `    "metadata": { "leadId": "L-92831", "campaign": "q3-outbound" }`,
        `  }'`,
      ].join("\n"),
    [base, connId, pnSid, activeApiId],
  );

  // A complete, runnable curl with a specific apiId + secret inlined — used in
  // the one-time reveal so the pair can never be mismatched.
  const buildCurl = (apiId: string, secret: string) =>
    [
      `curl -X POST "${base}/v1/dialer/connections/${connId}/numbers/${pnSid}/calls" \\`,
      `  -u "${apiId}:${secret}" \\`,
      `  -H "Content-Type: application/json" \\`,
      `  -d '{ "to": "+15551234567", "metadata": { "leadId": "L-92831" } }'`,
    ].join("\n");

  const responseJson = useMemo(
    () =>
      JSON.stringify(
        {
          id: "665f1c…",
          callSid: "CA…",
          connectionId: connId === "<CONNECTION_ID>" ? "665e…" : connId,
          phoneNumberSid: pnSid === "<PHONE_NUMBER_SID>" ? "PN…" : pnSid,
          to: "+15551234567",
          from: selected?.phoneNumber ?? "+1XXXXXXXXXX",
          status: "initiated",
          classification: { branch: null, decidedBy: null, phrase: null, decidedAt: null },
          metadata: { leadId: "L-92831", campaign: "q3-outbound" },
          createdAt: "2026-06-07T17:00:00.000Z",
        },
        null,
        2,
      ),
    [connId, pnSid, selected],
  );

  const handoffJson = useMemo(
    () =>
      JSON.stringify(
        {
          version: "1",
          event: "agentflow.handoff",
          tenantId: "665d…",
          channel: "voice",
          customer: { ref: "+15551234567" },
          reason: "Live human detected on outbound dialer call",
          summary: "The outbound dialer reached a live person at +15551234567.",
          metadata: { leadId: "L-92831", campaign: "q3-outbound" },
          emittedAt: "2026-06-07T17:00:14.000Z",
        },
        null,
        2,
      ),
    [],
  );

  return (
    <div className="mx-auto max-w-3xl space-y-8">
      <header>
        <Link
          to="/dialer"
          className="text-[12.5px] font-medium text-zinc-500 transition hover:text-zinc-800 dark:hover:text-zinc-300"
        >
          ← Screened Calls
        </Link>
        <p className="mt-2 text-[11.5px] font-semibold uppercase tracking-[0.16em] text-brand-600 dark:text-brand-400">
          Developers
        </p>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
          Dialer API
        </h1>
        <p className="mt-1 text-[13.5px] text-zinc-600 dark:text-zinc-400">
          Place outbound calls from your own code. We screen who answers — human, voicemail, or call
          screener — and hand humans to your configured destination with the context you sent.
        </p>
      </header>

      {/* Live-values banner */}
      {state.status === "ready" && !live && (
        <Banner tone="amber">
          No voice-capable numbers found yet. The example below uses placeholders.{" "}
          <Link to="/connections" className="font-medium underline underline-offset-2">
            Connect a Twilio account
          </Link>{" "}
          to see your real connection id and numbers.
        </Banner>
      )}
      {state.status === "error" && (
        <Banner tone="amber">
          Couldn&apos;t load your numbers — the example uses placeholders. The docs are still
          accurate.
        </Banner>
      )}

      {/* How it works */}
      <Section title="How it works">
        <ol className="space-y-2 text-[13.5px] text-zinc-700 dark:text-zinc-300">
          <Step n={1}>
            Your backend calls the endpoint below with a destination number and any context you want
            back later (<code className="font-mono text-[12.5px]">metadata</code>).
          </Step>
          <Step n={2}>
            We place the call from your dialer number and screen the answer in real time
            (AMD&nbsp;+&nbsp;Claude).
          </Step>
          <Step n={3}>
            On a <strong>human</strong>, your configured handoff fires (Flex task / webhook) with
            your <code className="font-mono text-[12.5px]">metadata</code> echoed back. Voicemail
            and screeners are handled automatically.
          </Step>
        </ol>
      </Section>

      {/* Auth */}
      <Section title="Authentication">
        <p className="text-[13.5px] text-zinc-700 dark:text-zinc-300">
          Use <strong>HTTP Basic auth</strong> with an API credential —{" "}
          <code className="font-mono text-[12.5px]">apiId</code> as the username,{" "}
          <code className="font-mono text-[12.5px]">secret</code> as the password (curl&apos;s{" "}
          <code className="font-mono text-[12.5px]">-u</code> flag). Requests are scoped to your
          tenant.
        </p>
        <CredentialsManager creds={creds} onChange={setCreds} buildCurl={buildCurl} />
      </Section>

      {/* Endpoint + curl */}
      <Section title="Place a call">
        <div className="flex flex-wrap items-center gap-2 text-[12.5px]">
          <span className="rounded bg-emerald-50 px-1.5 py-0.5 font-mono font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300">
            POST
          </span>
          <code className="break-all font-mono text-zinc-700 dark:text-zinc-300">
            /v1/dialer/connections/:connectionId/numbers/:phoneNumberSid/calls
          </code>
        </div>

        {live && numbers.length > 1 && (
          <label className="mt-3 block text-[12.5px]">
            <span className="mr-2 text-zinc-600 dark:text-zinc-400">Use number:</span>
            <select
              value={fromKey}
              onChange={(e) => setFromKey(e.target.value)}
              className="rounded-md border border-zinc-200 bg-white px-2 py-1 text-[12.5px] text-zinc-800 outline-none focus-visible:border-brand-500 focus-visible:shadow-ring dark:border-zinc-800 dark:bg-zinc-950/40 dark:text-zinc-200"
            >
              {numbers.map((n) => (
                <option key={keyOf(n)} value={keyOf(n)}>
                  {n.phoneNumber} · {n.connectionName}
                </option>
              ))}
            </select>
          </label>
        )}
        {live && selected && (
          <p className="mt-2 text-[12px] text-zinc-500">
            Placing from <span className="font-mono">{selected.phoneNumber}</span> ·{" "}
            {selected.connectionName}
            {live && (
              <span className="ml-2 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10.5px] font-medium text-emerald-700 ring-1 ring-inset ring-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300">
                live values
              </span>
            )}
          </p>
        )}

        <div className="mt-3">
          <CodeBlock code={curl} ariaLabel="Copy curl command" />
        </div>
      </Section>

      {/* Request fields */}
      <Section title="Request body">
        <FieldTable
          rows={[
            {
              name: "to",
              type: "string",
              required: true,
              desc: "Destination number (E.164, or a 10-digit US number we normalize).",
            },
            {
              name: "metadata",
              type: "object",
              required: false,
              desc: "Any JSON you want echoed back in the handoff (lead id, campaign, CRM ref…). ≤ 10KB.",
            },
          ]}
        />
      </Section>

      {/* Response */}
      <Section title="Response — 202 Accepted">
        <p className="mb-2 text-[13px] text-zinc-600 dark:text-zinc-400">
          The call is placed and screened asynchronously. Poll{" "}
          <code className="font-mono text-[12.5px]">GET /v1/dialer/calls/&#123;id&#125;</code> or
          watch the Screened Calls page for the outcome.
        </p>
        <CodeBlock code={responseJson} ariaLabel="Copy response example" />
      </Section>

      {/* Handoff payload */}
      <Section title="What you receive on a human">
        <p className="mb-2 text-[13px] text-zinc-600 dark:text-zinc-400">
          When a live person answers, your configured handoff fires. For the webhook provider, this
          signed JSON is POSTed to your URL (the Flex provider receives the same fields as task
          attributes). Your <code className="font-mono text-[12.5px]">metadata</code> comes back
          verbatim.
        </p>
        <CodeBlock code={handoffJson} ariaLabel="Copy handoff payload example" />
      </Section>
    </div>
  );
}

// ---------------------------------------------------------------------------

function CredentialsManager({
  creds,
  onChange,
  buildCurl,
}: {
  creds: ApiCredentialDTO[];
  onChange: (creds: ApiCredentialDTO[]) => void;
  buildCurl: (apiId: string, secret: string) => string;
}) {
  const [label, setLabel] = useState("");
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [justCreated, setJustCreated] = useState<{ apiId: string; secret: string } | null>(null);

  async function generate() {
    setGenerating(true);
    setError(null);
    try {
      const res = await api.apiCredentials.create({ label: label.trim() || undefined });
      setJustCreated({ apiId: res.credential.apiId, secret: res.secret });
      onChange([res.credential, ...creds]);
      setLabel("");
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : "Could not generate credential.");
    } finally {
      setGenerating(false);
    }
  }

  async function revoke(id: string) {
    try {
      const updated = await api.apiCredentials.revoke(id);
      onChange(creds.map((c) => (c.id === id ? updated : c)));
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : "Could not revoke credential.");
    }
  }

  return (
    <div className="space-y-3">
      {justCreated && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 dark:border-emerald-500/30 dark:bg-emerald-500/10">
          <p className="text-[12.5px] font-medium text-emerald-800 dark:text-emerald-300">
            Credential created — copy the secret now. You won&apos;t be able to see it again.
          </p>
          <div className="mt-2 space-y-2">
            <CodeBlock
              code={`apiId:  ${justCreated.apiId}\nsecret: ${justCreated.secret}`}
              ariaLabel="Copy new credential"
            />
            <div>
              <p className="mb-1 text-[11px] font-medium text-emerald-800 dark:text-emerald-300">
                Ready-to-run request (this exact pair):
              </p>
              <CodeBlock
                code={buildCurl(justCreated.apiId, justCreated.secret)}
                ariaLabel="Copy ready-to-run curl"
              />
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-end gap-2">
        <label className="text-[12.5px]">
          <span className="mb-1 block text-zinc-600 dark:text-zinc-400">Label (optional)</span>
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. backend-prod"
            disabled={generating}
            className="h-9 w-52 rounded-md border border-zinc-200 bg-white px-3 text-[13px] text-zinc-900 placeholder:text-zinc-400 outline-none transition focus-visible:border-brand-500 focus-visible:shadow-ring dark:border-zinc-800 dark:bg-zinc-950/40 dark:text-zinc-100 dark:placeholder:text-zinc-600 dark:focus-visible:border-brand-500/70"
          />
        </label>
        <Button size="sm" onClick={() => void generate()} disabled={generating}>
          {generating ? "Generating…" : "Generate credential"}
        </Button>
      </div>

      {error && (
        <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[12.5px] text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
          {error}
        </p>
      )}

      {creds.length > 0 && (
        <div className="overflow-hidden rounded-md border border-zinc-200 dark:border-zinc-800">
          <table className="w-full text-left text-[12.5px]">
            <thead className="bg-zinc-50 text-[10.5px] font-medium uppercase tracking-wider text-zinc-500 dark:bg-zinc-900/60">
              <tr>
                <th className="px-3 py-2">API id</th>
                <th className="px-3 py-2">Label</th>
                <th className="px-3 py-2">Status</th>
                <th className="hidden px-3 py-2 sm:table-cell">Last used</th>
                <th className="px-3 py-2" aria-label="Actions" />
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200 dark:divide-zinc-800/80">
              {creds.map((c) => (
                <tr key={c.id}>
                  <td className="px-3 py-2 align-middle font-mono text-zinc-800 dark:text-zinc-200">
                    {c.apiId}
                  </td>
                  <td className="px-3 py-2 align-middle text-zinc-600 dark:text-zinc-400">
                    {c.label ?? "—"}
                  </td>
                  <td className="px-3 py-2 align-middle">
                    <span
                      className={
                        "rounded-full px-1.5 py-0.5 text-[10.5px] font-medium ring-1 ring-inset " +
                        (c.status === "active"
                          ? "bg-emerald-50 text-emerald-700 ring-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300"
                          : "bg-zinc-100 text-zinc-500 ring-zinc-300 dark:bg-zinc-800/60 dark:text-zinc-500 dark:ring-zinc-700")
                      }
                    >
                      {c.status}
                    </span>
                  </td>
                  <td className="hidden px-3 py-2 align-middle text-zinc-500 sm:table-cell">
                    {c.lastUsedAt ? new Date(c.lastUsedAt).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-3 py-2 align-middle text-right">
                    {c.status === "active" && (
                      <button
                        type="button"
                        onClick={() => void revoke(c.id)}
                        className="text-[12px] text-red-600 hover:text-red-700 focus-visible:outline-none focus-visible:underline dark:text-red-400 dark:hover:text-red-300"
                      >
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="text-[15px] font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
        {title}
      </h2>
      {children}
    </section>
  );
}

function Step({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <li className="flex gap-3">
      <span className="flex h-5 w-5 flex-none items-center justify-center rounded-full bg-brand-50 text-[11px] font-semibold text-brand-700 ring-1 ring-inset ring-brand-500/30 dark:bg-brand-500/10 dark:text-brand-300">
        {n}
      </span>
      <span className="flex-1">{children}</span>
    </li>
  );
}

function CodeBlock({ code, ariaLabel }: { code: string; ariaLabel: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked — no-op */
    }
  }
  return (
    <div className="relative">
      <pre className="overflow-x-auto rounded-lg border border-zinc-800 bg-zinc-950 px-4 py-3 text-[12.5px] leading-relaxed text-zinc-100">
        <code className="font-mono">{code}</code>
      </pre>
      <button
        type="button"
        onClick={() => void copy()}
        aria-label={ariaLabel}
        className="absolute right-2 top-2 rounded-md border border-zinc-700 bg-zinc-800/80 px-2 py-1 text-[11px] font-medium text-zinc-200 transition hover:bg-zinc-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
      >
        {copied ? "Copied" : "Copy"}
      </button>
      <span className="sr-only" role="status" aria-live="polite">
        {copied ? "Copied to clipboard" : ""}
      </span>
    </div>
  );
}

interface FieldRow {
  name: string;
  type: string;
  required: boolean;
  desc: string;
}
function FieldTable({ rows }: { rows: FieldRow[] }) {
  return (
    <div className="overflow-hidden rounded-lg border border-zinc-200 dark:border-zinc-800">
      <table className="w-full text-left text-[13px]">
        <thead className="bg-zinc-50 text-[11px] font-medium uppercase tracking-wider text-zinc-500 dark:bg-zinc-900/60">
          <tr>
            <th className="px-3 py-2">Field</th>
            <th className="px-3 py-2">Type</th>
            <th className="px-3 py-2">Description</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-200 dark:divide-zinc-800/80">
          {rows.map((r) => (
            <tr key={r.name}>
              <td className="px-3 py-2 align-top">
                <span className="font-mono text-[12.5px] text-zinc-900 dark:text-zinc-100">
                  {r.name}
                </span>
                {r.required ? (
                  <span className="ml-1.5 text-[10.5px] font-medium text-red-600 dark:text-red-400">
                    required
                  </span>
                ) : (
                  <span className="ml-1.5 text-[10.5px] text-zinc-400">optional</span>
                )}
              </td>
              <td className="px-3 py-2 align-top font-mono text-[12px] text-zinc-500">{r.type}</td>
              <td className="px-3 py-2 align-top text-zinc-600 dark:text-zinc-400">{r.desc}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Banner({ tone, children }: { tone: "amber" | "zinc"; children: React.ReactNode }) {
  const cls =
    tone === "amber"
      ? "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300"
      : "border-zinc-200 bg-zinc-50 text-zinc-600 dark:border-zinc-800 dark:bg-zinc-900/40 dark:text-zinc-400";
  return <div className={"rounded-md border px-3 py-2 text-[12.5px] " + cls}>{children}</div>;
}
